import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import SuperAdminNav from '../../components/SuperAdminNav';

function getYouTubeThumbnail(url) {
  if (!url) return null;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s?]+)/);
  if (match) return `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg`;
  return null;
}

const emptyQuestion = () => ({
  question_text: '',
  input_type: 'text',
  point_value: '1',
  options: [],
  acceptable_answers: [],
  min_char_count: null,
});

export default function DemoDrills({ embedded = false }) {
  const { apiFetch } = useAuth();

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', youtube_url: '', completion_points: 20, extra_points: 5, bonus_criteria: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [populateMsg, setPopulateMsg] = useState('');
  const [libraryDrills, setLibraryDrills] = useState(null);
  const [showLibrary, setShowLibrary] = useState(false);
  const [librarySlot, setLibrarySlot] = useState(null);
  const [hasQuestions, setHasQuestions] = useState(false);
  const [questions, setQuestions] = useState([]);

  const loadSlots = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/demo-drills');
      setSlots(data.slots);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => { loadSlots(); }, [loadSlots]);

  const openEdit = async (slot) => {
    if (slot.empty) {
      setForm({ title: '', description: '', youtube_url: '', completion_points: 20, extra_points: 5, bonus_criteria: '' });
      setEditingSlot({ day_number: slot.day_number, isNew: true });
      setHasQuestions(false);
      setQuestions([]);
    } else {
      setForm({
        title: slot.title || '',
        description: slot.description || '',
        youtube_url: slot.youtube_url || '',
        completion_points: slot.completion_points || 20,
        extra_points: slot.extra_points || 5,
        bonus_criteria: slot.bonus_criteria || '',
      });
      setEditingSlot({ ...slot, isNew: false });
      // Load questions
      try {
        const data = await apiFetch(`/api/super/demo-drills/${slot.id}/questions`);
        if (data.questions && data.questions.length > 0) {
          setHasQuestions(true);
          setQuestions(data.questions.map(q => ({
            question_text: q.question_text,
            input_type: q.input_type,
            point_value: String(q.point_value),
            options: q.options || [],
            acceptable_answers: q.acceptable_answers || [],
            min_char_count: q.min_char_count || null,
          })));
        } else {
          setHasQuestions(false);
          setQuestions([]);
        }
      } catch (err) {
        setHasQuestions(false);
        setQuestions([]);
      }
    }
    setError('');
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    setSaving(true);
    setError('');
    try {
      let drillId;
      if (editingSlot.isNew) {
        const result = await apiFetch('/api/super/demo-drills', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...form, day_number: editingSlot.day_number }),
        });
        drillId = result.id;
      } else {
        await apiFetch(`/api/super/demo-drills/${editingSlot.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(form),
        });
        drillId = editingSlot.id;
      }
      // Save questions
      const questionsToSave = hasQuestions ? questions : [];
      await apiFetch(`/api/super/demo-drills/${drillId}/questions`, {
        method: 'PUT',
        body: JSON.stringify({ questions: questionsToSave }),
      });
      setEditingSlot(null);
      loadSlots();
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slot) => {
    if (!window.confirm(`Remove drill from Day ${slot.day_number}?`)) return;
    try {
      await apiFetch(`/api/super/demo-drills/${slot.id}`, { method: 'DELETE' });
      loadSlots();
    } catch (err) {
      console.error(err);
    }
  };

  const handlePopulate = async () => {
    setPopulateMsg('');
    try {
      const result = await apiFetch('/api/super/demo-drills/populate', { method: 'POST' });
      setPopulateMsg(result.message || `Added ${result.added} drills`);
      loadSlots();
    } catch (err) {
      setPopulateMsg(err.message || 'Failed to populate');
    }
  };

  const openLibraryPicker = async (dayNumber) => {
    setLibrarySlot(dayNumber);
    if (!libraryDrills) {
      try {
        const data = await apiFetch('/api/super/library');
        setLibraryDrills(data.drills || []);
      } catch (err) {
        console.error(err);
        setLibraryDrills([]);
      }
    }
    setShowLibrary(true);
  };

  const pickFromLibrary = async (drill) => {
    try {
      const pts = 20 + Math.floor(Math.random() * 11);
      const extra = 5 + Math.floor(Math.random() * 11);
      await apiFetch('/api/super/demo-drills', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          day_number: librarySlot,
          title: drill.title,
          description: drill.description,
          youtube_url: drill.youtube_url,
          completion_points: pts,
          extra_points: extra,
        }),
      });
      setShowLibrary(false);
      setLibrarySlot(null);
      loadSlots();
    } catch (err) {
      console.error(err);
    }
  };

  // Question helpers
  const addQuestion = () => {
    if (questions.length >= 20) return;
    setQuestions([...questions, emptyQuestion()]);
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'input_type') {
      if (value === 'text') {
        updated[index].options = [];
        if (updated[index].acceptable_answers.length === 0) updated[index].acceptable_answers = [];
      } else {
        updated[index].acceptable_answers = [];
        if (updated[index].options.length === 0) updated[index].options = [];
      }
    }
    setQuestions(updated);
  };

  const removeQuestion = (index) => setQuestions(questions.filter((_, i) => i !== index));

  const addOption = (qIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], options: [...updated[qIndex].options, { option_text: '', is_correct: false }] };
    setQuestions(updated);
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    const updated = [...questions];
    const opts = [...updated[qIndex].options];
    opts[oIndex] = { ...opts[oIndex], [field]: value };
    if (field === 'is_correct' && value && updated[qIndex].input_type === 'radio') {
      opts.forEach((o, i) => { if (i !== oIndex) opts[i] = { ...o, is_correct: false }; });
    }
    updated[qIndex] = { ...updated[qIndex], options: opts };
    setQuestions(updated);
  };

  const removeOption = (qIndex, oIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], options: updated[qIndex].options.filter((_, i) => i !== oIndex) };
    setQuestions(updated);
  };

  const addAcceptableAnswer = (qIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: [...updated[qIndex].acceptable_answers, { answer_text: '' }] };
    setQuestions(updated);
  };

  const updateAcceptableAnswer = (qIndex, aIndex, value) => {
    const updated = [...questions];
    const answers = [...updated[qIndex].acceptable_answers];
    answers[aIndex] = { ...answers[aIndex], answer_text: value };
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: answers };
    setQuestions(updated);
  };

  const removeAcceptableAnswer = (qIndex, aIndex) => {
    const updated = [...questions];
    updated[qIndex] = { ...updated[qIndex], acceptable_answers: updated[qIndex].acceptable_answers.filter((_, i) => i !== aIndex) };
    setQuestions(updated);
  };

  if (loading) return <div className="loading-spinner" style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><div className="spinner" /></div>;

  return (
    <div className={embedded ? '' : 'admin-layout'}>
      {!embedded && <SuperAdminNav activeItem="demos" />}

      <div className={embedded ? '' : 'admin-page'} style={embedded ? {} : { maxWidth: 900, margin: '0 auto', padding: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="page-title" style={{ margin: 0 }}>Manage Demo Account Drills</h1>
          <button className="btn btn-sm btn-blue" onClick={handlePopulate}>Auto-Populate from Library</button>
        </div>
        {populateMsg && <div style={{ padding: '8px 12px', background: '#1a1a1a', borderRadius: 8, marginBottom: 16, color: '#22c55e' }}>{populateMsg}</div>}

        <p style={{ color: '#888', fontSize: 14, marginBottom: 20 }}>
          These 20 drill slots rotate continuously for demo accounts. Day 10 is always "today" for demo players. Days 1-9 show as past drills, days 11-20 as upcoming.
        </p>

        <table className="super-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: 60 }}>Day</th>
              <th>Title</th>
              <th style={{ width: 70 }}>Video</th>
              <th style={{ width: 50 }}>Pts</th>
              <th style={{ width: 60 }}>Bonus</th>
              <th style={{ width: 30 }}>Q</th>
              <th style={{ width: 140 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slots.map(slot => (
              <tr key={slot.day_number} style={{ opacity: slot.empty ? 0.5 : 1 }}>
                <td style={{ fontWeight: 600, color: slot.day_number === 10 ? '#f77c00' : undefined }}>
                  {slot.day_number}{slot.day_number === 10 ? ' (today)' : ''}
                </td>
                <td>{slot.empty ? <span style={{ color: '#555', fontStyle: 'italic' }}>Empty slot</span> : slot.title}</td>
                <td>
                  {!slot.empty && slot.youtube_url ? (
                    <img src={getYouTubeThumbnail(slot.youtube_url)} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} />
                  ) : !slot.empty ? (
                    <span style={{ color: '#555' }}>None</span>
                  ) : null}
                </td>
                <td>{slot.empty ? '' : slot.completion_points}</td>
                <td>{slot.empty ? '' : slot.extra_points}</td>
                <td>{!slot.empty && slot.question_count > 0 ? <span style={{ color: '#f77c00' }}>{slot.question_count}</span> : ''}</td>
                <td>
                  {slot.empty ? (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-blue" onClick={() => openEdit(slot)}>Add</button>
                      <button className="btn btn-sm btn-outline" onClick={() => openLibraryPicker(slot.day_number)}>Library</button>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => openEdit(slot)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(slot)}>Remove</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Edit/Add modal */}
      {editingSlot && (
        <div className="modal-overlay" onClick={() => setEditingSlot(null)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>{editingSlot.isNew ? `Add Drill — Day ${editingSlot.day_number}` : `Edit Drill — Day ${editingSlot.day_number}`}</h3>
            {error && <div style={{ color: '#ef4444', marginBottom: 8 }}>{error}</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label>
                <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>Title</span>
                <input type="text" className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="Drill title" />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>Description</span>
                <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="Description" />
              </label>
              <label>
                <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>YouTube URL</span>
                <input type="text" className="form-input" value={form.youtube_url} onChange={e => setForm({ ...form, youtube_url: e.target.value })} placeholder="https://youtube.com/watch?v=..." />
              </label>
              <div style={{ display: 'flex', gap: 12 }}>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>Completion Points</span>
                  <input type="number" className="form-input" value={form.completion_points} onChange={e => setForm({ ...form, completion_points: parseInt(e.target.value, 10) || 0 })} />
                </label>
                <label style={{ flex: 1 }}>
                  <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>Extra Points</span>
                  <input type="number" className="form-input" value={form.extra_points} onChange={e => setForm({ ...form, extra_points: parseInt(e.target.value, 10) || 0 })} />
                </label>
              </div>
              <label>
                <span style={{ display: 'block', marginBottom: 4, fontSize: 13, color: '#888' }}>Bonus Criteria</span>
                <input type="text" className="form-input" value={form.bonus_criteria} onChange={e => setForm({ ...form, bonus_criteria: e.target.value })} placeholder="Optional" />
              </label>

              {/* Questions Toggle */}
              <label className="form-checkbox">
                <input type="checkbox" checked={hasQuestions} onChange={(e) => {
                  setHasQuestions(e.target.checked);
                  if (e.target.checked && questions.length === 0) setQuestions([emptyQuestion()]);
                }} />
                <span>Add Questions?</span>
              </label>

              {/* Question Builder */}
              {hasQuestions && (
                <div className="question-builder">
                  {questions.map((q, qi) => (
                    <div key={qi} className="question-card">
                      <div className="question-header">
                        <span className="question-number">Question {qi + 1}</span>
                        <button type="button" className="btn-remove" onClick={() => removeQuestion(qi)}>Remove</button>
                      </div>
                      <div className="form-group">
                        <label className="form-label">Question Text</label>
                        <input className="form-input" value={q.question_text} onChange={(e) => updateQuestion(qi, 'question_text', e.target.value)} placeholder="Enter your question..." />
                      </div>
                      <div className="form-row">
                        <div className="form-group form-group-half">
                          <label className="form-label">Input Type</label>
                          <select className="form-input" value={q.input_type} onChange={(e) => updateQuestion(qi, 'input_type', e.target.value)}>
                            <option value="text">Text Box</option>
                            <option value="radio">Radio Buttons</option>
                            <option value="checkbox">Checkboxes</option>
                          </select>
                        </div>
                        <div className="form-group form-group-half">
                          <label className="form-label">{q.input_type === 'checkbox' ? 'Points Per Correct' : 'Point Value'}</label>
                          <input className="form-input" type="number" min="1" value={q.point_value} onChange={(e) => updateQuestion(qi, 'point_value', e.target.value)} />
                        </div>
                      </div>

                      {q.input_type === 'text' && (
                        <div className="question-answers-section">
                          <label className="form-checkbox" style={{ marginBottom: 8 }}>
                            <input type="checkbox" checked={q.min_char_count != null} onChange={(e) => {
                              updateQuestion(qi, 'min_char_count', e.target.checked ? 50 : null);
                            }} />
                            <span>Allow any answer with a minimum character count</span>
                          </label>
                          {q.min_char_count != null ? (
                            <div className="form-group" style={{ marginTop: 4 }}>
                              <label className="form-label">Minimum Characters</label>
                              <input className="form-input" type="number" min="1" value={q.min_char_count} onChange={(e) => updateQuestion(qi, 'min_char_count', parseInt(e.target.value, 10) || 1)} style={{ width: 120 }} />
                            </div>
                          ) : (
                            <>
                              <label className="form-label">Acceptable Answers</label>
                              <div className="question-warning">Add every reasonable variation. Players must match one exactly (case-insensitive).</div>
                              {q.acceptable_answers.map((a, ai) => (
                                <div key={ai} className="option-row">
                                  <input className="form-input" value={a.answer_text} onChange={(e) => updateAcceptableAnswer(qi, ai, e.target.value)} placeholder="Acceptable answer..." />
                                  <button type="button" className="btn-remove-sm" onClick={() => removeAcceptableAnswer(qi, ai)}>x</button>
                                </div>
                              ))}
                              <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addAcceptableAnswer(qi)}>+ Add Answer</button>
                            </>
                          )}
                        </div>
                      )}

                      {q.input_type === 'radio' && (
                        <div className="question-answers-section">
                          <label className="form-label">Answer Options</label>
                          {q.options.map((o, oi) => (
                            <div key={oi} className="option-row">
                              <label className="option-correct-label" title="Correct answer">
                                <input type="radio" name={`q${qi}_correct`} checked={o.is_correct} onChange={() => updateOption(qi, oi, 'is_correct', true)} />
                              </label>
                              <input className="form-input" value={o.option_text} onChange={(e) => updateOption(qi, oi, 'option_text', e.target.value)} placeholder="Option text..." />
                              <button type="button" className="btn-remove-sm" onClick={() => removeOption(qi, oi)}>x</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addOption(qi)}>+ Add Option</button>
                        </div>
                      )}

                      {q.input_type === 'checkbox' && (
                        <div className="question-answers-section">
                          <label className="form-label">Answer Options</label>
                          <div className="question-warning">Players earn points for each correct answer they select.</div>
                          {q.options.map((o, oi) => (
                            <div key={oi} className="option-row">
                              <label className="option-correct-label" title="Correct answer">
                                <input type="checkbox" checked={o.is_correct} onChange={(e) => updateOption(qi, oi, 'is_correct', e.target.checked)} />
                              </label>
                              <input className="form-input" value={o.option_text} onChange={(e) => updateOption(qi, oi, 'option_text', e.target.value)} placeholder="Option text..." />
                              <button type="button" className="btn-remove-sm" onClick={() => removeOption(qi, oi)}>x</button>
                            </div>
                          ))}
                          <button type="button" className="btn btn-outline btn-sm add-option-btn" onClick={() => addOption(qi)}>+ Add Option</button>
                        </div>
                      )}
                    </div>
                  ))}
                  {questions.length < 20 && (
                    <button type="button" className="btn btn-outline btn-sm" onClick={addQuestion} style={{ width: '100%' }}>
                      + Add Question ({questions.length}/20)
                    </button>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button className="btn btn-outline" onClick={() => setEditingSlot(null)}>Cancel</button>
                <button className="btn btn-blue" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Library picker modal */}
      {showLibrary && (
        <div className="modal-overlay" onClick={() => setShowLibrary(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: '80vh', overflow: 'auto' }}>
            <h3 style={{ marginTop: 0 }}>Pick from Library &mdash; Day {librarySlot}</h3>
            {libraryDrills && libraryDrills.length === 0 && <p style={{ color: '#888' }}>No library drills found.</p>}
            {libraryDrills && libraryDrills.map(drill => (
              <div key={drill.id} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: '1px solid #2a2a2a', alignItems: 'center' }}>
                {drill.youtube_url ? (
                  <img src={getYouTubeThumbnail(drill.youtube_url)} alt="" style={{ width: 80, height: 60, borderRadius: 6, objectFit: 'cover', flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 80, height: 60, background: '#222', borderRadius: 6, flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{drill.title}</div>
                  <div style={{ color: '#888', fontSize: 12, marginTop: 2 }}>{(drill.categories || []).join(', ')}</div>
                </div>
                <button className="btn btn-sm btn-blue" onClick={() => pickFromLibrary(drill)}>Use</button>
              </div>
            ))}
            <div style={{ marginTop: 12, textAlign: 'right' }}>
              <button className="btn btn-outline" onClick={() => setShowLibrary(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
